import { useRef, useState } from 'react';

import {
  Button,
  Divider,
  Input,
  Modal,
  Select,
  Text,
  type TypeSelectOption,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { Caution, ModalContentsContainer, ModalSection, SearchFilter, Table, TableTopField } from '~/components/common';

import { useGetOutbounds, usePostShippingLabels } from '~/hooks/delivery-agency/api';
import { addIf } from '~/utils';
import {
  getDeliveryAgencyCreatedShippingLabelsColumnDefs,
  getDeliveryAgencyOutboundUnitColumnDefs,
} from '~/utils/delivery-agency/table';

import { DELIVERY_AGENCY, TOAST_MESSAGE } from '~/constants';

import type { TypeInputStatus } from '~/types/common.types';
import type {
  TypeDeliveryAgencyCreatedShippingLabel,
  TypeDeliveryAgencyOutboundUnit,
  TypeDeliveryAgencyOutboundUnitSearchFilterValues,
} from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyTrackingCreateShippingLabelModal.module.scss';

const KEYWORD_TYPE_OPTIONS: TypeSelectOption<string>[] = [
  {
    text: '출고번호',
    value: 'outboundCode',
  },
  {
    text: '운송장번호',
    value: 'shippingLabelNumber',
  },
];

const INITIAL_SEARCH_FILTER_VALUES: TypeDeliveryAgencyOutboundUnitSearchFilterValues = {
  keywordType: KEYWORD_TYPE_OPTIONS[0],
  keyword: '',
};

const DeliveryAgencyTrackingCreateShippingLabelModal = ({
  open,
  onPrintShippingLabels,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onPrintShippingLabels: (shippingLabelIds: number[], successCallback?: () => void | Promise<void>) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { requestOutbounds } = useGetOutbounds();
  const { createShippingLabels } = usePostShippingLabels();

  const [searchFilterValues, setSearchFilterValues] = useState<TypeDeliveryAgencyOutboundUnitSearchFilterValues>({
    ...INITIAL_SEARCH_FILTER_VALUES,
  });
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<{
    keyword: TypeInputStatus;
  }>({ keyword: 'default' });
  const [outboundUnit, setOutboundUnit] = useState<TypeDeliveryAgencyOutboundUnit | null>(null);
  const outboundUnitSearchedRef = useRef<boolean>(false);

  const [createdShippingLabels, setCreatedShippingLabels] = useState<TypeDeliveryAgencyCreatedShippingLabel[]>([]);

  const { columnDefs: outboundUnitColumnDefs, calculateTableHeight: calculateOutboundUnitTableHeight } =
    getDeliveryAgencyOutboundUnitColumnDefs();
  const { columnDefs: createdShippingLabelsColumnDefs } = getDeliveryAgencyCreatedShippingLabelsColumnDefs();

  const handleSearch = () => {
    if (searchFilterValues.keyword.trim() === '') {
      setSearchFilterValuesStatus({ keyword: 'error' });
      return;
    }

    requestOutbounds(
      {
        ...addIf(
          searchFilterValues.keywordType.value === 'outboundCode' && searchFilterValues.keyword,
          'outboundCode',
          searchFilterValues.keyword
        ),
        ...addIf(
          searchFilterValues.keywordType.value === 'shippingLabelNumber' && searchFilterValues.keyword,
          'shippingLabelNumber',
          searchFilterValues.keyword
        ),
      },
      {
        onSuccess: (data) => {
          setOutboundUnit(data);
          outboundUnitSearchedRef.current = true;
        },
        onError: (error) => {
          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handleSearchFilterReset = () => {
    setSearchFilterValues({ ...INITIAL_SEARCH_FILTER_VALUES });
    setSearchFilterValuesStatus({ keyword: 'default' });
  };

  const handleCreateShippingLabel = () => {
    if (outboundUnit === null) {
      toastService.addToast({ type: 'error', message: '주문을 찾을 수 없습니다.' });
      return;
    }

    if (!outboundUnit.isCreatableShippingLabel) {
      dialogService.addDialog({
        children:
          '해당 출고번호에 속한 모든 주문이\n주문취소 상태이거나 배송예정일이 내일이\n아닌 경우 추가 운송장 생성이 불가합니다.',
      });
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: `${outboundUnit.outboundCode} 출고번호에\n운송장 번호 1개를 추가로 생성하시겠습니까?`,
      onConfirm: () =>
        createShippingLabels(
          { outboundCode: outboundUnit.outboundCode },
          {
            onSuccess: (data) => {
              setOutboundUnit(null);
              setCreatedShippingLabels((prev) => [data, ...prev]);
              handleSearchFilterReset();
              outboundUnitSearchedRef.current = false;
            },
            onError: (error) => {
              if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
                dialogService.addDialog({ children: error?.message });
                return;
              }

              toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
            },
          }
        ),
    });
  };

  const handleConfirm = () => {
    if (createdShippingLabels.length === 0) return;

    dialogService.addDialog({
      type: 'confirm',
      children: `운송장 ${createdShippingLabels.length}개를 출력하시겠습니까?`,
      onConfirm: () => {
        const createdShippingLabelIds = createdShippingLabels.map((label) => label.shippingLabelId);
        onPrintShippingLabels(createdShippingLabelIds, onConfirm);
      },
    });
  };

  const handleCancel = () => {
    if (createdShippingLabels.length > 0) {
      dialogService.addDialog({
        type: 'confirm',
        children: '추가 운송장을 출력하지 않고\n페이지를 나가시겠습니까?',
        confirmText: '나가기',
        onConfirm: onCancel,
      });
      return;
    }

    onCancel();
  };

  return (
    <Modal
      title="추가 운송장 생성"
      size="xlarge"
      confirmText="출력"
      disabled={createdShippingLabels.length === 0}
      open={open}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    >
      <ModalContentsContainer gap={8}>
        <Caution
          contents={[
            {
              children:
                '이미 등록되어 있는 주문에 새로운 운송장 번호를 추가로 발급하는 기능입니다.\n이미 출력한 운송장을 재출력해야 할 경우 팝업 종료 후, 리스트 상단에 있는 [운송장 출력] 버튼을 이용해 주세요.',
            },
          ]}
          color={{ contentColor: 'kurly' }}
        />
        <SearchFilter onSubmit={handleSearch} onReset={handleSearchFilterReset}>
          <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
            <Text size="small" weight="semi-bold" element="p">
              키워드
            </Text>
            <div className={styles['input-container']}>
              <Select
                size="small"
                placeholder="선택"
                options={KEYWORD_TYPE_OPTIONS}
                value={searchFilterValues.keywordType}
                onChange={(_: unknown, value: TypeSelectOption<string>) => {
                  if (value?.value === searchFilterValues.keywordType?.value) return;
                  setSearchFilterValues({ ...searchFilterValues, keywordType: value, keyword: '' });
                  setSearchFilterValuesStatus({ keyword: 'default' });
                }}
              />
              <Input
                type="text"
                size="small"
                placeholder="입력"
                maxLength={50}
                status={searchFilterValuesStatus.keyword}
                value={searchFilterValues.keyword}
                onChange={(e) => {
                  setSearchFilterValues({ ...searchFilterValues, keyword: e.target.value });
                  setSearchFilterValuesStatus({ keyword: 'default' });
                }}
                onBlur={(e) => setSearchFilterValues({ ...searchFilterValues, keyword: e.target.value.trim() })}
                onFocus={() => setSearchFilterValuesStatus({ keyword: 'default' })}
              />
            </div>
          </div>
        </SearchFilter>
        <Divider color="gray100" />
        <div className={styles['shipping-label-table-container']}>
          <TableTopField
            rightButton={
              <Button
                size="tiny"
                color="secondary"
                disabled={outboundUnit === null}
                onClick={handleCreateShippingLabel}
              >
                운송장 생성
              </Button>
            }
          />
          <Table
            style={{
              height: calculateOutboundUnitTableHeight(outboundUnit?.shippingLabelNumbers),
            }}
            className={styles['shipping-label-table']}
            data={outboundUnit ? [outboundUnit] : []}
            options={{ columnDefs: outboundUnitColumnDefs }}
            rowStatusType={outboundUnitSearchedRef.current ? 'EMPTY' : 'DELIVERY_AGENCY_ADDITIONAL_SHIPPING_LABEL'}
          />
        </div>
        <Divider className={styles['section-divider']} color="gray100" />
        <ModalSection title="추가 운송장">
          <Table
            className={styles['additional-shipping-label-table']}
            data={createdShippingLabels}
            options={{ columnDefs: createdShippingLabelsColumnDefs }}
            rowStatusType="DELIVERY_AGENCY_ADDITIONAL_SHIPPING_LABEL_PRINT"
          />
        </ModalSection>
      </ModalContentsContainer>
    </Modal>
  );
};

export default DeliveryAgencyTrackingCreateShippingLabelModal;
